import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';

import Dialog from 'components/dialog';
import Switch from 'components/switch';
import Select from 'components/select';
import FlexBox from 'components/flex-box';
import TextField from 'components/text-field';
import NumberField from 'components/number-field';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';

import { usePost } from 'hooks/use-post';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { CreateFeederInput } from '../types/create-feeder-input.type';
import { kgToLbs, lbsToKg } from 'views/self-service/utils/convertLbsKg';
import { Feeder } from 'views/self-service/features/feeders/types/feeders.type';
import { postFeeder } from 'views/self-service/features/feeders/api/post-feeder';
import { isValidDecimal, isValidInteger, isValidPositive } from 'utils/validate-numbers';
import { useFeedersState } from 'views/self-service/features/feeders/context/feeders-context';

type CreateFeederProps = {};

const MAX_STORAGE_CAPACITY = 500;
const MAX_SUPPORTED_FEEDING_BIOMASS = 15000;

const PAGE_NAME_ID = 'selfService.feeders';

const CreateFeeder: React.FC<CreateFeederProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const { feederBrands } = useFeedersState();
  const snackbarDispatch = useSnackbarDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  //#region STATE
  const [enableKG, setEnableKg] = useState<boolean>(false);
  //#endregion STATE

  //#region FORMS
  const { handleSubmit, control, getValues, setValue } = useForm<CreateFeederInput>({
    defaultValues: {
      name: '',
      feederBrandId: '',
      storageCapacity: '',
      feedingCapacity: '',
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  //#region POST DATA
  const createFeeder = usePost<Feeder, { request: CreateFeederInput }>(
    (request) => postFeeder(account.company!.companyId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEnableKg(false);
      queryClient.invalidateQueries({
        queryKey: ['getFeeders']
      });
      dialogRef?.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HANDLERS
  const handleCreateFeeder = (request: CreateFeederInput) => {
    createFeeder.mutate({
      request: {
        ...request,
        storageCapacity: Number(request.storageCapacity),
        feedingCapacity: enableKG ? Number(request.feedingCapacity) : Math.round(lbsToKg(Number(request.feedingCapacity))),
      },
    });
  };

  const onKeyDown = (e: any) => e.stopPropagation();

  const calculateBiomass = () => {
    let biomass = getValues('feedingCapacity');
    let convertedBiomass = enableKG ? kgToLbs(Number(biomass)) : Math.round(lbsToKg(Number(biomass)));

    setValue('feedingCapacity', convertedBiomass === 0 ? '' : convertedBiomass);
  };

  //#endregion HANDLERS

  return (
    <Box flexDirection={'column'} display={'flex'} gap={3} mb={3}>
      <Dialog
        maxWidth="sm"
        ref={dialogRef}
        sx={{ zIndex: 1510 }}
        onClose={() => setEnableKg(false)}
        onKeyDown={(e) => e.key === 'Tab' && e.stopPropagation()}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.title` })}
        button={<Button fullWidth>{intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.btnTitle` })}</Button>}>
        <>
          {createFeeder.isPending && <LoadingSpinner overlay />}
          <FlexBox>
            <TextField
              dark
              fullWidth
              stopPropagation
              onKeyDown={onKeyDown}
              controller={{
                name: 'name',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.name` })}
            />
            <Select
              dark
              nested
              fullWidth
              controller={{
                name: 'feederBrandId',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                  },
                },
              }}
              noResults={{
                show: !feederBrands?.length,
                text: <FormattedMessage id={'general.noResults'} />,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.brand` })}>
              {feederBrands?.map((brand) => (
                <RadioSelectItem key={brand.id} value={brand.id}>
                  {brand.name}
                </RadioSelectItem>
              ))}
            </Select>
            <NumberField
              dark
              fullWidth
              onKeyDown={onKeyDown}
              endAdornment={'kg'}
              controller={{
                name: 'storageCapacity',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidPositive(value) ? true : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      isValidInteger(value) && Number(value) <= MAX_STORAGE_CAPACITY
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_STORAGE_CAPACITY }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.storageCapacity` })}
            />
            <NumberField
              dark
              fullWidth
              onKeyDown={onKeyDown}
              endAdornment={intl.formatMessage(
                { id: `${PAGE_NAME_ID}.createFeeder.dialog.endAdornment` },
                { unit: enableKG ? 'kg' : 'lbs' }
              )}
              controller={{
                name: 'feedingCapacity',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (value: number) =>
                      isValidDecimal(value) && Math.round(lbsToKg(value))
                        ? true
                        : intl.formatMessage({ id: 'form.rules.invalidValue' }),
                    between: (value: number) =>
                      Math.round(lbsToKg(value)) || (isValidInteger(value) && Number(value) <= MAX_SUPPORTED_FEEDING_BIOMASS)
                        ? true
                        : intl.formatMessage({ id: 'form.rules.between' }, { lower: 0, upper: MAX_SUPPORTED_FEEDING_BIOMASS }),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.supportedBiomass` })}
            />
            <Switch
              dark
              value={enableKG}
              onChange={() => {
                setEnableKg((prev) => !prev);
                calculateBiomass();
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeeder.dialog.switchLabel` })}
            />
            <Box display={'flex'} mt={5} justifyContent={'end'}>
              <Button type="button" fullWidth={isMobile} onClick={handleSubmit((d) => handleCreateFeeder(d))}>
                <FormattedMessage id={`${PAGE_NAME_ID}.createFeeder.dialog.button`} />
              </Button>
            </Box>
          </FlexBox>
        </>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateFeeder);
